// On-device receipt field extraction. OCR text stays client-side; this module
// only derives review candidates, which a person must confirm before a bill
// is created. No network calls and no posting live here.

type ReceiptFields = {
  vendor: string | null;
  date: string | null;
  total_cents: number | null;
  reference: string | null;
  issues: string[];
};

const MONEY = /(?:\$\s*)?(\d{1,3}(?:,\d{3})*|\d+)\.(\d{2})/g;
const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

const GENERIC_VENDOR_LINES = new Set([
  "order summary", "gmail", "thank you", "thank you for your purchase",
  "payment receipt confirmation", "receipt confirmation", "transaction summary",
  "order placed", "order details", "invoice", "receipt", "payment", "total",
  "subtotal", "to", "from", "date", "order", "shipping", "billing information",
]);

function amountsOn(line: string): { cents: number; index: number }[] {
  const found: { cents: number; index: number }[] = [];
  for (const match of line.matchAll(MONEY)) {
    const whole = Number.parseInt(match[1]!.replace(/,/g, ""), 10);
    const cents = whole * 100 + Number.parseInt(match[2]!, 10);
    if (Number.isSafeInteger(cents)) found.push({ cents, index: match.index ?? 0 });
  }
  return found;
}

function isoDate(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (year < 2020 || year > 2035 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** First plausible calendar date with a year in the extracted text. */
function receiptDate(text: string): string | null {
  const patterns: Array<{ regex: RegExp; parse: (m: RegExpMatchArray) => string | null }> = [
    { regex: /\b(\d{4})-(\d{2})-(\d{2})\b/g, parse: (m) => isoDate(Number(m[1]), Number(m[2]), Number(m[3])) },
    { regex: /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g, parse: (m) => isoDate(Number(m[3]), Number(m[1]), Number(m[2])) },
    { regex: /\b(\d{1,2})\/(\d{1,2})\/(\d{2})\b/g, parse: (m) => { const year = Number(m[3]); return isoDate(year < 100 ? 2000 + year : year, Number(m[1]), Number(m[2])); } },
    { regex: /\b(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2}),?\s*(\d{4})\b/gi, parse: (m) => isoDate(Number(m[3]), MONTHS[m[1]!.toLowerCase()] ?? 0, Number(m[2])) },
  ];
  const found: { at: number; value: string }[] = [];
  for (const { regex, parse } of patterns) {
    for (const match of text.matchAll(regex)) {
      const value = parse(match);
      if (value) found.push({ at: match.index ?? 0, value });
    }
  }
  found.sort((a, b) => a.at - b.at);
  return found[0]?.value ?? null;
}

/** The final payable total: a grand total wins, then the last total/amount due. */
function receiptTotal(text: string): number | null {
  const lines = text.split(/\r?\n/);
  const all: { cents: number; grand: boolean; at: number }[] = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    if (/sub\s*total/i.test(line)) continue;
    if (!/\b(grand\s+total|total\s+amount\s+paid|amount\s+due|balance\s+due|order\s+total|total)\b/i.test(line)) continue;
    const same = amountsOn(line);
    const amounts = same.length > 0 ? same : amountsOn(lines[index + 1] ?? "");
    const last = amounts[amounts.length - 1];
    if (last) all.push({ cents: last.cents, grand: /grand\s+total/i.test(line), at: index });
  }
  const grand = all.filter((entry) => entry.grand);
  const pool = grand.length > 0 ? grand : all;
  return pool.length > 0 ? pool[pool.length - 1]!.cents : null;
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
    .replace(/\b(Usa|Llc|Inc|Pbc)\b/g, (word) => word.toUpperCase());
}

const GENERIC_VENDOR_WORDS = new Set([
  "ship", "shipping", "to", "from", "payment", "method", "order", "summary",
  "total", "subtotal", "receipt", "invoice", "thank", "you", "gmail", "placed",
  "date", "details", "information", "page", "message", "contact", "support",
  "tax", "taxes", "estimated", "united", "states", "arriving", "delivery",
  "delivered", "return", "eligible", "item", "items", "before", "after",
  "description", "amount", "customer", "billing", "confirmation",
]);

function vendorLike(line: string): string | null {
  const cleaned = line.replace(/[®™©]/g, "").replace(/\s+/g, " ").trim();
  if (cleaned.length < 3 || cleaned.length > 40) return null;
  if (!/^[A-Za-z][A-Za-z0-9 &'.-]*$/.test(cleaned)) return null;
  if (/\d{3,}/.test(cleaned)) return null;
  const lower = cleaned.toLowerCase();
  if (GENERIC_VENDOR_LINES.has(lower)) return null;
  const words = lower.split(/[^a-z]+/).filter(Boolean);
  // A receipt metadata line is not a vendor name: any generic word disqualifies it.
  if (words.some((word) => GENERIC_VENDOR_WORDS.has(word))) return null;
  return titleCase(cleaned);
}

/** Best-effort vendor: a receipt email/URL domain, else a company-like line. */
function receiptVendor(text: string): string | null {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  for (const line of lines.slice(0, 15)) {
    const email = line.match(/[\w.+-]+@([\w-]+(?:\.[\w-]+)+)/);
    const url = line.match(/\b(?:https?:\/\/)?(?:www\.)?([\w-]+)\.(com|net|org|co|io|gov)\b/i);
    const domain = (email?.[1] ?? url?.[1] ?? "").toLowerCase();
    if (!domain) continue;
    const parts = domain.split(".");
    const name = parts.length >= 2 ? parts[parts.length - 2] : parts[0];
    if (!name || name.length < 3 || /^\d+$/.test(name)) continue;
    if (["gmail", "yahoo", "outlook", "google", "microsoft"].includes(name)) continue;
    return titleCase(name);
  }
  for (const line of lines.slice(0, 8)) {
    // Amount and date tails are not part of the vendor name.
    const stripped = line.replace(/\s+\$?\d[\d,]*\.\d{2}.*$/, "").trim();
    const vendor = vendorLike(stripped);
    if (vendor) return vendor;
  }
  return null;
}

/** "Order # 114-2564497-0994601" / "ORDER 108546" style reference. */
function receiptReference(text: string): string | null {
  const match = text.match(/(?:order|invoice|receipt)\s*(?:#|no\.?|number)?\s*[:#]?\s*([A-Za-z0-9-]*\d[A-Za-z0-9-]*)/i);
  const value = match?.[1]?.replace(/-$/, "") ?? null;
  return value && value.length >= 5 ? value : null;
}

export function parseReceiptText(text: string): ReceiptFields {
  const vendor = receiptVendor(text);
  const date = receiptDate(text);
  const total_cents = receiptTotal(text);
  const reference = receiptReference(text);
  const issues: string[] = [];
  if (!vendor) issues.push("Vendor could not be read; enter it before creating a bill.");
  if (!date) issues.push("Receipt date could not be read; enter it before creating a bill.");
  if (total_cents === null) issues.push("Receipt total could not be read; enter it before creating a bill.");
  else if (total_cents <= 0) issues.push("Receipt total must be positive.");
  return { vendor, date, total_cents, reference, issues };
}
